import React from 'react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 500, 1000];

const C = {
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    bg:     'rgba(6,11,31,0.65)',
};

const btnBase = {
    padding: '5px 10px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${C.border}`,
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.75)',
    transition: 'all 0.15s',
    lineHeight: 1,
    minWidth: 32,
};
const btnActive = {
    ...btnBase,
    background: `rgba(0,212,255,0.15)`,
    border: `1px solid rgba(0,212,255,0.4)`,
    color: C.cyan,
    fontWeight: 800,
};
const btnDisabled = {
    ...btnBase,
    opacity: 0.3,
    cursor: 'not-allowed',
};

/**
 * Pagination bar.
 *
 * Props:
 *   page        – current 1-based page number
 *   pages       – total page count
 *   total       – total record count (shown as info)
 *   limit       – current page size
 *   onPage(n)   – called with new page number
 *   onLimit(n)  – called with new limit
 *   style       – optional wrapper style
 */
const Pagination = ({ page = 1, pages = 1, total = 0, limit = 10, onPage, onLimit, style }) => {
    if (pages <= 0) return null;

    const goTo = n => { if (n >= 1 && n <= pages) onPage?.(n); };

    // Build visible page numbers (up to 7 around current)
    const getPageNums = () => {
        const nums = [];
        if (pages <= 7) {
            for (let i = 1; i <= pages; i++) nums.push(i);
        } else {
            nums.push(1);
            const start = Math.max(2, page - 2);
            const end   = Math.min(pages - 1, page + 2);
            if (start > 2) nums.push('...');
            for (let i = start; i <= end; i++) nums.push(i);
            if (end < pages - 1) nums.push('...');
            nums.push(pages);
        }
        return nums;
    };

    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to   = Math.min(page * limit, total);

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            ...style,
        }}>
            {/* Page-size selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>Rows:</span>
                <select
                    value={limit}
                    onChange={e => { onLimit?.(Number(e.target.value)); onPage?.(1); }}
                    style={{
                        background: C.bg, color: 'rgba(255,255,255,0.8)',
                        border: `1px solid ${C.border}`, borderRadius: 8,
                        padding: '4px 6px', fontSize: 12, cursor: 'pointer', outline: 'none',
                    }}
                >
                    {PAGE_SIZE_OPTIONS.map(n => (
                        <option key={n} value={n}>{n}</option>
                    ))}
                </select>
            </div>

            {/* Info */}
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                {from}–{to} of {total}
            </span>

            {/* Nav buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button style={page === 1 ? btnDisabled : btnBase} onClick={() => goTo(1)} disabled={page === 1} title="First">«</button>
                <button style={page === 1 ? btnDisabled : btnBase} onClick={() => goTo(page - 1)} disabled={page === 1} title="Previous">‹</button>

                {getPageNums().map((n, i) =>
                    n === '...'
                        ? <span key={`e${i}`} style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', padding: '0 3px' }}>…</span>
                        : <button
                            key={n}
                            style={n === page ? btnActive : btnBase}
                            onClick={() => goTo(n)}
                        >{n}</button>
                )}

                <button style={page === pages ? btnDisabled : btnBase} onClick={() => goTo(page + 1)} disabled={page === pages} title="Next">›</button>
                <button style={page === pages ? btnDisabled : btnBase} onClick={() => goTo(pages)} disabled={page === pages} title="Last">»</button>
            </div>
        </div>
    );
};

export default Pagination;
